import { z } from "zod";
import { useCacheSlice } from "../hooks";
import type { ApiClient } from "../types";

const entryFilterOptionSchema = z.object({
	value: z.string(),
	label: z.string(),
	sublabel: z.string().optional(),
});

const entryFilterOptionsSchema = z.object({
	object: z.literal("entry_filters"),
	currentTimekeeperId: z.string().optional(),
	status: z.array(z.string()),
	application: z.array(z.string()),
	matter: z.array(entryFilterOptionSchema),
	client: z.array(z.string()),
	billableCategory: z.array(z.string()),
	task: z.array(z.string()),
	activity: z.array(z.string()),
	taskCode: z.array(entryFilterOptionSchema),
	activityCode: z.array(entryFilterOptionSchema),
	timekeeper: z.array(entryFilterOptionSchema),
});

export type EntryFilterOption = z.infer<typeof entryFilterOptionSchema>;
export type EntryFilterOptions = z.infer<typeof entryFilterOptionsSchema>;

export const EMPTY_ENTRY_FILTERS: EntryFilterOptions = {
	object: "entry_filters",
	currentTimekeeperId: undefined,
	status: [],
	application: [],
	matter: [],
	client: [],
	billableCategory: [],
	task: [],
	activity: [],
	taskCode: [],
	activityCode: [],
	timekeeper: [],
};

function parseEntryFilters(data: unknown): EntryFilterOptions {
	return entryFilterOptionsSchema.parse(data);
}

export async function fetchEntryFilters(
	api: ApiClient,
): Promise<EntryFilterOptions> {
	const res = await api.makeApiRequest({
		endpoint: "/v2/entries/filters",
		method: "GET",
	});
	if (res.status === "ERROR")
		throw new Error(res.error || "Failed to fetch entry filters");
	return parseEntryFilters(res.data);
}

export function getEntryFilterTimekeeperIds(
	filters: EntryFilterOptions | null | undefined,
): string[] {
	if (!filters) return [];
	const seen = new Set<string>();
	const ids: string[] = [];
	for (const option of filters.timekeeper) {
		const id = option.value.trim();
		if (!id || seen.has(id)) continue;
		seen.add(id);
		ids.push(id);
	}
	return ids;
}

export function useEntryFiltersCache() {
	const { data, isLoading, lastFetchedAt } = useCacheSlice("entry_filters");
	const entryFilters = (data ?? EMPTY_ENTRY_FILTERS) as EntryFilterOptions;
	const isInitialLoading = isLoading && lastFetchedAt === 0;
	return { entryFilters, isLoading: isInitialLoading, lastFetchedAt };
}
